<template>
  <AppDialog
    :model-value="isOpen"
    @update:model-value="$emit('update:isOpen', $event)"
    :dismissible="false"
    :show-close-button="false"
    :show-default-footer="false"
    variant="chest"
  >
    <div class="relative w-full h-full overflow-hidden chest-stage">

      <!-- ===== Le décor : la vidéo elle-même =====
           Le montage porte sa propre grotte, donc plus de fond composé côté CSS
           ni de `mix-blend-mode`. Reste à choisir le cadre, et c'est un
           arbitrage mesuré : la source fait 558×754 (ratio 0,74) quand un écran
           de téléphone tourne autour de 0,46. Remplir la hauteur imposerait de
           rogner 19 % de chaque côté, ce qui tranche le coffre en plein milieu
           de ses ferrures. Ne rien rogner du tout laisse en revanche le bas de
           l'écran vide. Le cadre 5:8 est le point d'équilibre : il occupe 74 %
           de la hauteur, ne rogne que 8 % de chaque côté, et le coffre y tient
           entier avec de la marge. -->
      <div class="chest-video-frame">
        <video
          ref="videoRef"
          class="chest-video absolute inset-0 w-full h-full object-cover"
          :class="{ 'cursor-pointer': phase === 'idle' }"
          aria-hidden="true"
          playsinline
          webkit-playsinline
          preload="none"
          disablepictureinpicture
          disableremoteplayback
          @playing="posterVisible = false"
          @ended="finishOpening"
          @click="handleTap"
        >
          <source :src="CHEST_VIDEO" type="video/mp4" />
        </video>

        <!-- Première image, en calque distinct plutôt qu'en attribut `poster`.
             L'attribut est retiré par le navigateur dès l'APPEL à `play()`, pas
             à la première image décodée : entre les deux, la vidéo est un
             rectangle noir, et ce trou se voyait au clic. Ici c'est l'événement
             `playing` qui déclenche le fondu, donc la relève est garantie. Les
             premières secondes du montage étant le coffre immobile, la
             superposition est indiscernable. -->
        <img
          :src="CHEST_POSTER"
          alt=""
          aria-hidden="true"
          class="chest-poster absolute inset-0 w-full h-full object-cover pointer-events-none"
          :class="{ 'chest-poster-out': !posterVisible }"
        />
      </div>

      <!-- ===== Le panneau bas : dégradé + contenu =====
           Présent à l'arrêt et à la révélation, absent pendant la lecture pour
           laisser l'animation respirer en entier. -->
      <Transition
        enter-active-class="transition-opacity duration-500 ease-out"
        enter-from-class="opacity-0"
        leave-active-class="transition-opacity duration-300 ease-in"
        leave-to-class="opacity-0"
      >
        <div
          v-if="phase !== 'opening'"
          class="chest-panel absolute inset-x-0 bottom-0 z-10 flex flex-col items-center justify-end min-h-[35%] px-4 pt-16"
          :style="{ paddingBottom: 'max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 16px)' }"
        >
          <!-- ===== À l'arrêt : ouvrir, ou remettre à plus tard =====
               Le coffre n'est pas une interruption à subir : le refuser le
               laisse simplement dans le stock (chests.is_claimed = false), d'où
               le bloc de la page Récompenses le ressortira. La phrase sous le bouton
               dit où il atterrit, sinon « Plus tard » se lit comme « jeter ». -->
          <template v-if="phase === 'idle'">
            <Button
              expand="block"
              size="lg"
              class="chest-cta w-full"
              data-testid="chest-open"
              @click="handleTap"
            >
              {{ $t('chest.open', 'Open Chest') }}
            </Button>

            <button
              type="button"
              class="chest-later"
              data-testid="chest-later"
              @click="handleLater"
            >
              <LucideIcon name="clock" :size="18" class="chest-later-icon" />
              <span>{{ $t('chest.later', 'Later') }}</span>
            </button>
          </template>

          <!-- ===== Après l'animation : ce qu'on a gagné ===== -->
          <template v-else-if="chest">
            <h2
              class="text-3xl font-bold text-white text-center"
              style="text-shadow: 0 0 24px rgba(251,191,36,0.7), 0 2px 8px rgba(0,0,0,0.5)"
            >
              {{ titleText }}
            </h2>

            <!-- Précise que le coffre vient de la série de l'enfant -->
            <p
              v-if="chest.trigger_type === 'streak'"
              class="mt-2 text-center text-base text-white/80 px-2"
            >
              {{ t('chest.earnedFromStreak', { days: chest.trigger_value }) }}
            </p>

            <ChestReward :chest="chest" :is-onboarding="isOnboarding" class="w-full" />

            <!-- Bouton Claim -->
            <Button
              v-if="!isClaiming && !hasClaimed"
              expand="block"
              size="lg"
              class="chest-cta w-full mt-3"
              data-testid="chest-claim"
              @click="handleClaim"
            >
              {{ $t('chest.claim', 'Claim Reward') }}
            </Button>

            <!-- État : en cours de claim -->
            <div
              v-else-if="isClaiming"
              class="flex items-center justify-center gap-2 py-4"
            >
              <Spinner class="text-white" />
              <span class="text-white/70">{{ $t('chest.claiming', 'Claiming...') }}</span>
            </div>

            <!-- État : claim réussi -->
            <div
              v-else-if="hasClaimed"
              class="flex flex-col items-center justify-center gap-2 py-4 text-green-400"
            >
              <LucideIcon name="circle-check" :size="36" class="animate-[scale-in_0.3s_ease-out]" />
              <span class="font-bold text-lg">{{ $t('common.success', 'Success!') }}</span>
            </div>
          </template>
        </div>
      </Transition>

    </div>
  </AppDialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import Spinner from '@/components/common/Spinner.vue';
import { useI18n } from 'vue-i18n';
import AppDialog from '@/components/common/AppDialog.vue';
import { Button } from '@/components/ui/button';
import ChestReward from '@/components/chest/ChestReward.vue';
import { chestService } from '@/services/chest.service';
import { feedbackService } from '@/services/feedback.service';
import { useHaptics } from '@/composables/useHaptics';
import type { Chest } from '@/models/Chest';

const { t } = useI18n();
const haptics = useHaptics();

const CHEST_VIDEO = '/videos/chest/chest3.mp4';
/** Première image du montage, affichée pendant toute la phase d'attente. */
const CHEST_POSTER = '/videos/chest/chest3-poster.jpg';

const VIDEO_DURATION_MS = 4042;

/**
 * Repères du montage (4,042 s, 24 i/s), exprimés en SECONDES DE LECTURE et non
 * en délai depuis le clic. La distinction est tout : entre `play()` et la
 * première image réellement rendue il s'écoule un temps variable (décodage,
 * mise en route du son, tampon), et des minuteries parties du clic décalaient
 * donc tous les retours de cette latence. On lit `currentTime` à la place, ce
 * qui recale aussi les choses si la lecture bégaie en cours de route.
 *
 * Le coffre s'ébroue avant d'éclater, et le mouvement se mesure : centre de
 * masse horizontal relevé image par image, il part à droite (+10 px à 0,14 s),
 * revient plus loin à gauche (-12 px à 0,50 s), redonne un petit coup à droite
 * (+5 px à 0,645 s) puis se pose jusqu'à l'ouverture. Un retour par extrême,
 * d'intensité décroissante comme le mouvement, plutôt qu'un seul au passage par
 * le centre où il ne se passe précisément rien.
 */
const BEAT_SHAKE_RIGHT_S = 0.14;
const BEAT_SHAKE_LEFT_S = 0.5;
const BEAT_SHAKE_SETTLE_S = 0.645;
const BEAT_BURST_S = 1.15;
const BEAT_COINS_PEAK_S = 1.9;
/**
 * La récompense n'attend pas la fin du plan. Passé 3 s le coffre est posé, les
 * pièces sont retombées et la dernière seconde n'est plus que le rayonnement :
 * le panneau monte par-dessus pendant que le montage se termine, son compris.
 */
const BEAT_REVEAL_S = 3.04;
/** Filet si la lecture n'avance pas du tout (décodage KO, onglet en arrière-plan). */
const REVEAL_FALLBACK_MS = VIDEO_DURATION_MS + 900;

const props = withDefaults(
  defineProps<{
    isOpen: boolean;
    chest: Chest | null;
    /** When true, claim does not call the API (e.g. onboarding demo). */
    isOnboarding?: boolean;
    /** Custom reveal title (e.g. onboarding surprise chest) instead of the trigger-based one. */
    titleOverride?: string;
  }>(),
  { isOnboarding: false, titleOverride: undefined }
);

const emit = defineEmits<{
  'update:isOpen': [value: boolean];
  'claim-complete': [];
  /** « Plus tard » : l'hôte peut écarter aussi les coffres qui attendent. */
  later: [];
}>();

type Phase = 'idle' | 'opening' | 'opened';

const phase = ref<Phase>('idle');
const isClaiming = ref(false);
const hasClaimed = ref(false);

const videoRef = ref<HTMLVideoElement | null>(null);
/** Le calque d'affiche couvre la vidéo tant qu'elle n'a pas d'image à montrer. */
const posterVisible = ref(true);
let openTimers: ReturnType<typeof setTimeout>[] = [];
let beatFrame = 0;
let nextBeat = 0;

/**
 * Les temps forts, dans l'ordre de lecture. Lus par `pumpBeats`, qui compare la
 * position réelle de la vidéo à `at` plutôt que de faire confiance à l'horloge.
 */
const BEATS: Array<{ at: number; fire: () => void }> = [
  { at: BEAT_SHAKE_RIGHT_S, fire: () => void haptics.impactMedium() },
  { at: BEAT_SHAKE_LEFT_S, fire: () => void haptics.impactMedium() },
  { at: BEAT_SHAKE_SETTLE_S, fire: () => void haptics.impactLight() },
  { at: BEAT_BURST_S, fire: () => void haptics.impactHeavy() },
  { at: BEAT_COINS_PEAK_S, fire: () => void haptics.notificationSuccess() },
];

function clearOpenTimers(): void {
  openTimers.forEach(clearTimeout);
  openTimers = [];
  if (beatFrame) {
    cancelAnimationFrame(beatFrame);
    beatFrame = 0;
  }
}

/**
 * Suit la lecture et déclenche les retours au passage de chaque repère. La
 * boucle relit `currentTime` à chaque image de l'écran, donc les retours
 * restent collés à ce qui est affiché même si la lecture démarre en retard ou
 * bégaie. Une boucle `while` et non un `if` : si une image saute, on rattrape
 * les repères franchis d'un coup plutôt que de les perdre.
 */
const pumpBeats = (): void => {
  beatFrame = 0;
  const video = videoRef.value;
  if (!video || phase.value !== 'opening') return;

  const t = video.currentTime;
  while (nextBeat < BEATS.length && t >= BEATS[nextBeat].at) {
    BEATS[nextBeat].fire();
    nextBeat += 1;
  }

  if (t >= BEAT_REVEAL_S) {
    finishOpening();
    return;
  }
  beatFrame = requestAnimationFrame(pumpBeats);
};

/**
 * Title shown when the reward is revealed.
 * A streak chest celebrates the streak that earned it; otherwise we keep the
 * generic level-up title.
 */
const titleText = computed(() => {
  if (props.titleOverride) return props.titleOverride;
  if (props.chest?.trigger_type === 'streak') {
    return t('chest.streakTitle', { days: props.chest.trigger_value });
  }
  return t('chest.title', 'Level Up!');
});

// Reset all state whenever the modal reopens
watch(
  () => props.isOpen,
  (newVal) => {
    clearOpenTimers();

    const video = videoRef.value;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }

    if (newVal) {
      phase.value = 'idle';
      isClaiming.value = false;
      hasClaimed.value = false;
      posterVisible.value = true;
      if (video) {
        // Le montage porte sa propre bande-son : on repart systématiquement du
        // son actif, même si une ouverture précédente a dû se replier sur muet.
        video.muted = false;
        // Le chargement se déclenche à l'OUVERTURE et pas au montage : la modale
        // est montée en permanence dans App.vue, et un préchargement passif
        // téléchargerait le rush à chaque démarrage, même sans coffre à ouvrir.
        // L'enfant vise le bouton pendant ce temps, donc le tap arrive chargé.
        video.preload = 'auto';
        video.load();
      }
    }
  }
);

onUnmounted(clearOpenTimers);

/**
 * Place la vidéo sur sa dernière image. Sert aux chemins qui ne jouent pas le
 * montage : la récompense s'affiche alors sur un coffre OUVERT et non sur le
 * coffre fermé du début, qui raconterait le contraire de ce qui vient d'arriver.
 */
const seekToEnd = (video: HTMLVideoElement): void => {
  // Sans image décodée, la vidéo est un rectangle noir : on garde l'affiche,
  // même figée sur le coffre fermé, plutôt que de révéler du vide.
  if (video.readyState < 2 /* HAVE_CURRENT_DATA */) return;

  const end = Number.isFinite(video.duration) && video.duration > 0
    ? video.duration
    : VIDEO_DURATION_MS / 1000;
  // Un cheveu avant la borne : certains décodeurs y rendent une image vide.
  video.currentTime = Math.max(0, end - 0.05);
  posterVisible.value = false;
};

/**
 * Ouverture : on lance le montage en entier et on suit son rythme. Le bouton
 * remplace le déclencheur, pas l'attente : l'animation se joue jusqu'au bout.
 */
const handleTap = () => {
  if (phase.value !== 'idle') return;
  phase.value = 'opening';

  // Accusé de réception immédiat du bouton. Il ne se brouille plus avec le
  // premier coup du coffre : celui-ci est désormais calé sur la position réelle
  // de la lecture, qui démarre après le temps de décodage, et non 140 ms après
  // le clic quoi qu'il arrive.
  void haptics.impactLight();

  const video = videoRef.value;
  if (!video) {
    // Pas d'élément vidéo (rendu dégradé) : on ne laisse jamais l'enfant devant
    // un coffre qui ne s'ouvre pas.
    finishOpening();
    return;
  }

  // Mouvement réduit : le montage est une explosion de lumière plein écran, soit
  // exactement ce que ce réglage demande d'éviter. On va droit à la récompense,
  // sur l'image finale du coffre ouvert.
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    seekToEnd(video);
    finishOpening();
    return;
  }

  video.currentTime = 0;
  const playback = video.play();
  if (playback) {
    playback.catch(() => {
      // Refus le plus probable : la plateforme n'autorise pas le son ici. On
      // retente sans, plutôt que de priver l'enfant de l'animation. Si même ça
      // échoue (décodage, fichier absent), on révèle la récompense directement.
      video.muted = true;
      video.play().catch(() => {
        seekToEnd(video);
        finishOpening();
      });
    });
  }

  // Retours haptiques et révélation : pilotés par la position réelle de la
  // lecture, en complément du son.
  nextBeat = 0;
  beatFrame = requestAnimationFrame(pumpBeats);

  // Filets, pour le cas où la lecture n'avance pas du tout : `ended` d'un côté
  // (voir le template), ce délai de l'autre.
  openTimers.push(setTimeout(finishOpening, REVEAL_FALLBACK_MS));
};

/**
 * « Plus tard » : on referme sans rien réclamer. Le coffre reste
 * `is_claimed = false`, donc dans le stock durable, et le bloc de la page Récompenses
 * le rouvrira au moment choisi. Rien à supprimer côté base : c'est le parent
 * (RewardNotificationQueue) qui décide quoi faire de la célébration.
 */
const handleLater = () => {
  if (phase.value !== 'idle') return;
  void haptics.impactLight();
  emit('later');
  emit('update:isOpen', false);
};

/**
 * Fin de l'ouverture : la vidéo reste sur sa dernière image et sert de fond au
 * panneau qui revient. Appelée par `ended`, par le filet de sécurité ou par les
 * chemins dégradés, d'où la garde d'idempotence.
 */
const finishOpening = () => {
  if (phase.value === 'opened') return;
  clearOpenTimers();
  phase.value = 'opened';
  void haptics.impactLight();
};

const handleClaim = async () => {
  if (!props.chest?.id || isClaiming.value || hasClaimed.value) return;

  isClaiming.value = true;

  if (props.isOnboarding) {
    hasClaimed.value = true;
    handleClaimComplete();
    return;
  }

  try {
    await chestService.claimChest(props.chest.child_id, props.chest.id);
    hasClaimed.value = true;
    handleClaimComplete();
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to claim chest';
    const alreadyClaimed = errorMessage.includes('already claimed');

    if (alreadyClaimed) {
      // Another device claimed it — silently close
      hasClaimed.value = true;
      handleClaimComplete();
      return;
    }

    const { toast } = await import('vue-sonner');
    toast.error(
      t('chest.error.claimFailed', 'Failed to claim reward'),
      { description: errorMessage }
    );
    isClaiming.value = false;
  }
};

/**
 * Filet de fermeture après réclamation.
 *
 * Il DOIT vivre dans `openTimers`, donc mourir avec la session d'ouverture.
 * En dehors, il survivait à la fermeture : le parent referme la modale dès
 * `claim-complete`, enchaîne sur le coffre suivant de la file, et cette
 * minuterie orpheline tirait 2 s plus tard une fermeture qui atteignait le
 * coffre SUIVANT. Vu de l'enfant, le deuxième coffre s'ouvrait puis se
 * refermait tout seul, et sa notification partait avec.
 */
const handleClaimComplete = () => {
  // Coffre de série ouvert = déclencheur secondaire de la demande d'avis
  // native (TW #17712331). Différé pour laisser la modale se fermer ; toutes
  // les autres gardes (parent uniquement, quotas store) vivent dans le service.
  if (!props.isOnboarding && props.chest?.trigger_type === 'streak') {
    setTimeout(() => feedbackService.recordStreakChestOpened(), 2600);
  }
  emit('claim-complete');
  openTimers.push(
    setTimeout(() => {
      phase.value = 'idle';
      isClaiming.value = false;
      hasClaimed.value = false;
      emit('update:isOpen', false);
    }, 2000)
  );
};
</script>

<style scoped>
/* Sol de la scène. Réglé sur le bord de l'image au repos (#120B06 en bas,
   #100C09 en haut) pour que la vidéo ne se détache jamais d'un cadre plus clair
   ou plus froid qu'elle. */
.chest-stage {
  background: #0F0A06;
}

/* Cadre 5:8, ancré en haut. Ratio FIXE et non celui de la source : c'est lui qui
   détermine combien `cover` rogne sur les côtés, donc il ne doit pas dépendre de
   l'écran. À 5:8 la fenêtre visible couvre 9 % → 93,5 % de la largeur d'origine,
   et le coffre y tient de 16 % à 92 %. Le décalage à 58 % rattrape le fait que
   le coffre n'est pas centré dans le plan, il est peint légèrement à droite. */
.chest-video-frame {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  aspect-ratio: 5 / 8;
  max-height: 100%;
}

/* En paysage (l'iPad n'est pas verrouillé, le web non plus) c'est la hauteur qui
   contraint. On pilote alors par elle et on laisse la largeur suivre le ratio,
   sinon `cover` réduirait la grotte à une bande horizontale. */
@media (orientation: landscape) {
  .chest-video-frame {
    width: auto;
    height: 100%;
    max-width: 100%;
  }
}

/* Le bord bas de l'image passe de #120B06 au repos à #5A4723 au moment de
   l'éclat : la grotte s'illumine. Un fond fixe en dessous ferait donc apparaître
   une ligne franche pile sur le temps fort. Le fondu transforme cette arête en
   dégradé, quelle que soit la luminosité de l'image à cet instant.
   Fondu vers la couleur à alpha 0 et non vers `transparent` : WebKit interpole
   en prémultiplié et fait apparaître une bande grise. */
.chest-video,
.chest-poster {
  object-position: 58% center;
  -webkit-mask-image: linear-gradient(to bottom, #000 90%, rgba(0, 0, 0, 0) 100%);
  mask-image: linear-gradient(to bottom, #000 90%, rgba(0, 0, 0, 0) 100%);
}

/* Le fondu est déclenché par `playing`, donc au moment où la vidéo a vraiment
   une image à afficher. 320 ms suffisent : les deux calques montrent le même
   coffre fermé, le fondu n'a rien à masquer, il évite juste une bascule sèche. */
.chest-poster {
  opacity: 1;
  transition: opacity 320ms ease-out;
}

.chest-poster-out {
  opacity: 0;
}

/* Le dégradé est porté par le panneau lui-même : il s'étend donc juste ce qu'il
   faut, court quand il n'y a qu'un bouton, haut quand la récompense est là. */
.chest-panel {
  background: linear-gradient(
    to top,
    #0F0A06 0%,
    rgba(15, 10, 6, 0.97) 32%,
    rgba(15, 10, 6, 0.82) 58%,
    rgba(15, 10, 6, 0.38) 82%,
    rgba(15, 10, 6, 0) 100%
  );
}

/* Bouton large, atteignable au pouce. Dégradé doré sur texte sombre pour rester
   lisible et festif au-dessus de la grotte. Vrai CSS : le <Button> est un
   <button> ordinaire et non un ion-button en shadow DOM, donc les anciennes
   variables --background/--color ne s'appliquent plus. Le !important passe
   devant les classes utilitaires de thème du composant. */
.chest-cta {
  background: linear-gradient(180deg, #fde047 0%, #f59e0b 100%) !important;
  color: #422006 !important;
  border: none !important;
  border-radius: 18px !important;
  box-shadow: 0 8px 24px rgba(245, 158, 11, 0.4);
  padding-top: 18px;
  padding-bottom: 18px;
  height: auto;
  font-weight: 800;
  font-size: 1.05rem;
  letter-spacing: 0.01em;
  text-transform: none;
}

.chest-cta:active {
  background: #f59e0b !important;
}

/* Sortie secondaire. Elle doit se lire comme un BOUTON (un enfant qui déchiffre
   à peine ne reconnaît pas un simple mot posé là comme une action), sans jamais
   disputer l'or du bouton principal : verre dépoli et filet clair sur la
   grotte, contre un aplat doré juste au-dessus. La pastille horaire dit
   « plus tard » avant même que le mot soit lu.
   `justify-content` et non `text-align` : les resets de bouton mettent
   `text-align: inherit`, et le panneau étant aligné à gauche, le label partait
   se coller au bord. */
.chest-later {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  margin-top: 12px;
  padding: 14px 16px;
  color: rgba(255, 255, 255, 0.92);
  font-weight: 700;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 18px;
  transition: background-color 150ms ease-out, transform 150ms ease-out;
}

.chest-later-icon {
  color: rgba(253, 224, 71, 0.85);
}

.chest-later:active {
  background: rgba(255, 255, 255, 0.14);
  transform: scale(0.985);
}
</style>
